
import Seo from './Seo';

const PrivacyPage = () => {

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <Seo
        title="Privacy Policy - Uumail"
        description="Read how Uumail protects account data, email aliases, forwarded messages, and privacy controls."
        path="/privacy"
      />
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-sm p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">UUMail Privacy Policy</h1>
          <p className="text-gray-600 mb-8">Effective Date: May 31, 2025</p>
          <p className="text-gray-700 mb-8">
            UUMail ("we", "our", "us") is committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy outlines how we collect, use, store, and disclose your data when you use our services.
          </p>

          <div className="space-y-12">
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Our Core Privacy Commitments</h2>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>No email content storage without consent: We do not store your email content unless you explicitly opt in to a specific feature that requires it (e.g., cloud sync).</li>
                <li>No data sharing with third parties: We do not sell, rent, or share your personal information or email data with any third party.</li>
                <li>No tracking for advertising: We do not engage in behavioral tracking or targeted advertising based on your data.</li>
                <li>User control: You always retain full control over your data and may access, delete, or modify it at any time.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>
              <p className="text-gray-700 mb-4">We minimize data collection and only gather the following:</p>
              
              <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">2.1. Account Information</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Email address, username, encrypted password</li>
                <li>Optional: recovery email or phone (if provided)</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">2.2. Metadata (for service operation only)</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Timestamps, IP addresses (used temporarily for anti-abuse/security)</li>
                <li>Logs of system errors and delivery status</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">2.3. We Do NOT Collect:</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Email content, unless explicitly authorized</li>
                <li>Contacts, address book, or calendar data</li>
                <li>Any third-party identifiers (e.g., advertising IDs)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
              <p className="text-gray-700 mb-4">We process personal data only for the following lawful purposes:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>To operate and maintain the UUMail service</li>
                <li>To authenticate users and prevent abuse</li>
                <li>To respond to customer support requests</li>
                <li>To comply with legal obligations when required</li>
              </ul>
              
              <p className="text-gray-700 mt-4">We rely on the following legal bases (per GDPR Article 6):</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Consent (where applicable)</li>
                <li>Performance of a contract</li>
                <li>Legal obligation</li>
                <li>Legitimate interest, where applicable and balanced</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Data Retention</h2>
              <p className="text-gray-700 mb-4">We retain your personal data only for as long as necessary to fulfill the purposes outlined above. Specifically:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Account data is kept until the user deletes their account</li>
                <li>Temporary metadata (like IP addresses) is retained for a maximum of 30 days for security purposes</li>
                <li>Logs and analytics data are anonymized or deleted regularly</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Data Security</h2>
              <p className="text-gray-700 mb-4">We use strong encryption, strict access controls, and industry-standard security practices to protect your data.</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Data in transit is encrypted using HTTPS/TLS</li>
                <li>Passwords are hashed using industry best practices</li>
                <li>Access to servers is tightly restricted to authorized personnel</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Your Rights</h2>
              <p className="text-gray-700 mb-4">Depending on your jurisdiction, you may have the right to:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Access your data</li>
                <li>Delete or correct inaccurate data</li>
                <li>Withdraw consent at any time</li>
                <li>Request data portability (GDPR only)</li>
                <li>File a complaint with a data protection authority</li>
              </ul>
              <p className="text-gray-700 mt-4">To exercise your rights, contact us at: privacy@uumail.com</p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. International Data Transfers</h2>
              <p className="text-gray-700">
                We may store and process your data on servers located in multiple jurisdictions, including countries outside your residence (e.g., Singapore, the United States). We ensure that appropriate safeguards (such as Standard Contractual Clauses) are in place for international data transfers.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Children's Privacy</h2>
              <p className="text-gray-700">
                UUMail is not intended for users under the age of 13 (or equivalent minimum age in your jurisdiction). We do not knowingly collect data from children.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Third-Party Services</h2>
              <p className="text-gray-700">
                We do not integrate third-party advertising or analytics services that collect your data. If we rely on third-party infrastructure (e.g., for email routing), we ensure they are contractually obligated to respect your privacy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Policy Changes</h2>
              <p className="text-gray-700">
                We may update this Privacy Policy to reflect changes in legal requirements or our practices. We will notify you via app or email before any material changes take effect.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Contact Us</h2>
              <p className="text-gray-700">
                If you have any questions, concerns, or requests regarding your privacy or this policy, please contact:
              </p>
              <div className="mt-4">
                <p className="text-gray-700 font-medium">UUMail Privacy Office</p>
                <a href="mailto:privacy@uumail.com" className="text-blue-600 hover:text-blue-700">
                  Email: privacy@uumail.com
                </a>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
